import React, { useEffect, useState, useCallback, useMemo, useRef } from 'react';
import {
  fetchHistoryEntries, restoreHistoryEntry, duplicateAsNewList,
  deleteListNow, clearConflictPendingReview,
} from '../lib/historyConflict';
import { hasConflictMarker, stripConflictMarkerKeepBoth } from '../lib/mergeConflict';
import { mergeThreeWay } from '../lib/threeWayMerge';
import { diffLines } from '../lib/lineDiff';

// HistoryPanel — side-by-side comparison + restore for a list's previous
// versions. Walter 2026-05-16: backs the "B" conflict-detection promise
// — every overwritten version is recoverable here.
//
// Lifecycle: opens on demand (via the history icon in the list header),
// fetches the history subcollection one-shot (no live subscription, no
// per-list listener cost), and lets the user view + restore any entry.
//
// Restore is non-destructive: the live doc's current content is itself
// snapshotted to history before being overwritten. So if the user picks
// the wrong version, the version they just displaced is still there.
//
// Props:
//   - open / onClose             — modal visibility
//   - uid, boardId, listId       — Firestore path
//   - listTitle                  — display only
//   - currentContent / currentTitle — live values from the parent's
//     local state (post-debounce), shown in the left pane
//   - actorUid                   — who is doing the restore (for audit)
//   - pushToast                  — optional toast on restore success

export default function HistoryPanel({
  open, onClose,
  uid, boardId, listId,
  listTitle,
  currentContent, currentTitle,
  actorUid,
  pushToast,
  // Optional: live list doc, used by "save as new list" to seed
  // position/size for the duplicate.
  sourceList,
  // Walter 2026-05-17: callback that writes content back to the live
  // list. Used by the "merge into one list" action (which transforms
  // the current content) and by the undo paths of replace/merge
  // (which revert to a pre-action snapshot we close over). If
  // omitted, those actions are hidden / undo is skipped.
  onApplyContent,
  // Walter 2026-09-14 (fix 2): called after ANY action that resolves the
  // conflict (replace, mark resolved, merge both, save as separate list),
  // so the panel can hide its band at once instead of waiting seconds for
  // its snapshot to catch up with the write.
  onResolved,
}) {
  // Held in a ref so the resolution callbacks always call the latest
  // onResolved without it joining their dependency arrays.
  const onResolvedRef = useRef(onResolved);
  onResolvedRef.current = onResolved;
  const [entries, setEntries] = useState(null); // null = loading, [] = none
  const [selectedId, setSelectedId] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  // Walter 2026-05-17: on narrow viewports the explainer ate most of
  // the screen leaving tiny diff panes. Track viewport width so we
  // can collapse it behind a "show help" toggle on phones.
  const [vw, setVw] = useState(() =>
    (typeof window !== 'undefined') ? window.innerWidth : 1024,
  );
  const [showExplainer, setShowExplainer] = useState(false);
  useEffect(() => {
    if (!open) return undefined;
    const onResize = () => setVw(window.innerWidth);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, [open]);
  const isNarrow = vw < 640;

  // One-shot fetch each time the panel opens. Keeps it fresh without a
  // permanent listener; if the user wants the absolute newest entries
  // they can close + reopen, which is cheap.
  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    setEntries(null);
    setSelectedId(null);
    setError(null);
    (async () => {
      try {
        const list = await fetchHistoryEntries({ uid, boardId, listId });
        if (cancelled) return;
        setEntries(list);
        if (list.length > 0) setSelectedId(list[0].id);
      } catch (e) {
        if (cancelled) return;
        console.warn('[HistoryPanel] fetch failed', e);
        setError('couldn’t load previous versions');
        setEntries([]);
      }
    })();
    return () => { cancelled = true; };
  }, [open, uid, boardId, listId]);

  const selectedEntry = entries && selectedId
    ? entries.find(e => e.id === selectedId)
    : null;

  // Walter 2026-05-17: line-level diff between current (left) and
  // selected previous version (right) so each pane can render its
  // own lines with per-line colour coding.
  const diff = useMemo(() => {
    if (!selectedEntry) return null;
    return diffLines(currentContent || '', selectedEntry.content || '');
  }, [currentContent, selectedEntry]);

  const handleRestore = useCallback(async () => {
    if (!selectedEntry || busy) return;
    setBusy(true);
    // Walter 2026-05-17: capture pre-replace state for the undo
    // toast. closing over it means undo doesn't need to refetch.
    const preReplaceContent = currentContent;
    const preReplaceTitle = currentTitle;
    try {
      await restoreHistoryEntry({
        uid, boardId, listId,
        entry: { content: selectedEntry.content, title: selectedEntry.title },
        actorUid,
      });
      if (pushToast) {
        pushToast(
          'replaced with the previous version. undo to bring back what you had.',
          // undoAction: revert via onApplyContent if available; otherwise
          // call restoreHistoryEntry against the just-snapshotted pre-replace
          // state. (Falling back to onApplyContent is preferred because it
          // is synchronous from the user's perspective.)
          onApplyContent
            ? () => onApplyContent(preReplaceContent, preReplaceTitle)
            : null,
          { ttlMs: 12000 },
        );
      }
      onResolvedRef.current?.();
      onClose?.();
    } catch (e) {
      console.warn('[HistoryPanel] restore failed', e);
      setError('replace failed — try again');
    } finally {
      setBusy(false);
    }
  }, [selectedEntry, busy, uid, boardId, listId, actorUid, pushToast, onClose,
      currentContent, currentTitle, onApplyContent]);

  // Walter 2026-05-17: "mark as resolved" — primary action when
  // current content is what the user wants to keep. Just clears the
  // band by clearing conflictPendingReview on the list doc. If the
  // list still has the legacy `// CONFLICTING EDITS` marker (from
  // pre-2026-05-17 conflicts that ran the prepend-marker path), we
  // also strip the marker via stripConflictMarkerKeepBoth so the
  // content cleans itself up.
  const handleMarkResolved = useCallback(async () => {
    if (busy) return;
    setBusy(true);
    const preResolveContent = currentContent;
    try {
      let cleanContent;
      if (hasConflictMarker(currentContent)) {
        cleanContent = stripConflictMarkerKeepBoth(currentContent);
        if (onApplyContent) onApplyContent(cleanContent);
      }
      await clearConflictPendingReview({
        uid, boardId, listId,
        cleanContent: cleanContent ?? undefined,
      });
      if (pushToast) {
        pushToast(
          'marked as resolved. previous versions stay in history.',
          // Undo restores the marker-content (if we stripped) and
          // we'd want to re-set the flag — but writing the flag back
          // would require another helper. For the common case the
          // user simply re-opens the modal; pre-clean content is
          // available via undo when applicable.
          (cleanContent !== undefined && onApplyContent)
            ? () => onApplyContent(preResolveContent)
            : null,
          { ttlMs: 12000 },
        );
      }
      onResolvedRef.current?.();
      onClose?.();
    } catch (e) {
      console.warn('[HistoryPanel] mark-resolved failed', e);
      setError('couldn’t mark as resolved — try again');
    } finally {
      setBusy(false);
    }
  }, [busy, currentContent, uid, boardId, listId, onApplyContent, pushToast, onClose]);

  // Walter 2026-06-22: "merge both (beta)" — a true 3-way line merge. The
  // conflict snapshot carries the BASE (the content both versions descended
  // from); we merge current (mine) + the selected previous (theirs) against it,
  // so disjoint edits union and deletions are respected, only a same-line clash
  // keeps both. Writes the merged content + clears the band, like mark-resolved
  // but combined. Only shown when the snapshot has a base (conflicts created
  // after this shipped). Labelled beta — Walter dogfoods it on the go.
  const canMergeBoth = !!selectedEntry && selectedEntry.baseContent != null;
  const handleMergeBoth = useCallback(async () => {
    if (!canMergeBoth || busy) return;
    setBusy(true);
    const preMerge = currentContent;
    try {
      const { merged } = mergeThreeWay(
        selectedEntry.baseContent ?? '',
        currentContent ?? '',
        selectedEntry.content ?? '',
      );
      if (onApplyContent) onApplyContent(merged);
      // Snapshot the pre-merge CURRENT to history before overwriting it, so a
      // bad beta merge is always recoverable (the previous version is already
      // in history; this preserves the live one too). Then clear the band.
      await restoreHistoryEntry({
        uid, boardId, listId,
        entry: { content: merged, title: currentTitle },
        actorUid,
      });
      await clearConflictPendingReview({ uid, boardId, listId });
      if (pushToast) {
        pushToast(
          'merged both versions (beta). undo to revert.',
          onApplyContent ? () => onApplyContent(preMerge) : null,
          { ttlMs: 12000 },
        );
      }
      onResolvedRef.current?.();
      onClose?.();
    } catch (e) {
      console.warn('[HistoryPanel] merge-both failed', e);
      setError('merge failed — try again');
    } finally {
      setBusy(false);
    }
  }, [canMergeBoth, busy, selectedEntry, currentContent, currentTitle, actorUid,
      uid, boardId, listId, onApplyContent, pushToast, onClose]);

  // Walter 2026-05-17: "keep both — save as new list" — save the
  // selected previous version as a brand-new list, leaving the
  // current list untouched. Direct answer to "what if I want to
  // preserve both sets of edits?"
  const handleSaveAsNewList = useCallback(async () => {
    if (!selectedEntry || busy) return;
    setBusy(true);
    try {
      const { id: newListId } = await duplicateAsNewList({
        uid, boardId,
        entry: { content: selectedEntry.content, title: selectedEntry.title },
        sourceList,
      });
      // Walter 2026-05-17: save-as-separate is also a resolution
      // choice — clear the band so the user isn't nagged after.
      await clearConflictPendingReview({ uid, boardId, listId }).catch(() => {});
      if (pushToast) {
        pushToast(
          'saved the previous version as a new list. undo to remove it.',
          newListId
            ? async () => {
                try { await deleteListNow({ uid, boardId, listId: newListId }); }
                catch (err) { console.warn('[HistoryPanel] undo delete failed', err); }
              }
            : null,
          { ttlMs: 12000 },
        );
      }
      onResolvedRef.current?.();
      onClose?.();
    } catch (e) {
      console.warn('[HistoryPanel] save-as-new failed', e);
      setError('save as new list failed — try again');
    } finally {
      setBusy(false);
    }
  }, [selectedEntry, busy, uid, boardId, listId, sourceList, pushToast, onClose]);

  if (!open) return null;

  return (
    <div
      onClick={onClose}
      style={{
        position: 'fixed', inset: 0, zIndex: 3000,
        background: 'rgba(0,0,0,0.75)',
        backdropFilter: 'blur(4px)',
        display: 'flex', alignItems: 'flex-start', justifyContent: 'center',
        // Walter 2026-05-17: less top padding on mobile so the modal
        // has more vertical room for the stacked panes + scrolling.
        paddingTop: isNarrow ? '12px' : '60px',
      }}
    >
      <div
        onClick={e => e.stopPropagation()}
        style={{
          background: 'var(--surface)',
          border: '1px solid var(--border-bright)',
          borderRadius: '6px',
          width: '880px', maxWidth: '94vw',
          maxHeight: isNarrow ? 'calc(100vh - 24px)' : 'calc(100vh - 120px)',
          boxShadow: 'var(--shadow-modal-deep)',
          display: 'flex', flexDirection: 'column',
          overflow: 'hidden',
        }}
      >
        {/* Header */}
        <div style={{
          padding: '14px 18px',
          borderBottom: '1px solid var(--border)',
          background: 'var(--surface2)',
        }}>
          <div style={{
            fontFamily: 'var(--mono)', fontSize: '12px', fontWeight: 600,
            color: 'var(--accent)', marginBottom: '4px',
            display: 'flex', alignItems: 'center', justifyContent: 'space-between',
            gap: '8px',
          }}>
            <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              previous versions of "{listTitle || 'untitled'}"
            </span>
            {isNarrow && (
              <button
                onClick={() => setShowExplainer(v => !v)}
                style={{
                  background: 'transparent',
                  color: 'var(--text-dim)',
                  border: '1px solid var(--border-bright)',
                  borderRadius: 'var(--radius)',
                  fontFamily: 'var(--mono)', fontSize: '10px',
                  padding: '2px 6px', cursor: 'pointer', flexShrink: 0,
                }}
              >
                {showExplainer ? 'hide help' : 'help'}
              </button>
            )}
          </div>
          {(!isNarrow || showExplainer) && (
            <div style={{
              fontFamily: 'var(--mono)', fontSize: '11px',
              color: 'var(--text-dim)', lineHeight: 1.5,
            }}>
              current is on the left, the cloud's pre-merge version is
              on the right. green = lines unique to that side. choose:
              <br/>
              • <b>use current and mark resolved</b> — keep current
              as it is and dismiss the band. (most common — you've
              reviewed and current is what you want.)
              <br/>
              • <b>replace current with previous version</b> — swap in
              the right side. (the displaced live content gets
              snapshotted to history too, so undo always works.)
              <br/>
              • <b>save previous as separate list</b> — keep current
              as-is and place the picked version as a separate list
              alongside.
            </div>
          )}
        </div>

        {/* Entry picker (dropdown + meta) */}
        <div style={{
          padding: '10px 18px',
          borderBottom: '1px solid var(--border)',
          background: 'var(--surface2)',
          display: 'flex', gap: '12px', alignItems: 'center',
          flexShrink: 0,
        }}>
          <label style={{
            fontFamily: 'var(--mono)', fontSize: '11px',
            color: 'var(--text-dim)',
          }}>
            version:
          </label>
          <select
            value={selectedId || ''}
            onChange={e => setSelectedId(e.target.value)}
            disabled={!entries || entries.length === 0}
            style={{
              background: 'var(--bg)',
              color: 'var(--text)',
              border: '1px solid var(--border-bright)',
              borderRadius: 'var(--radius)',
              fontFamily: 'var(--mono)', fontSize: '11px',
              padding: '4px 8px',
              flex: 1, minWidth: 0,
            }}
          >
            {entries === null && <option>loading…</option>}
            {entries && entries.length === 0 && <option>(no previous versions)</option>}
            {entries && entries.map((e, idx) => (
              <option key={e.id} value={e.id}>
                {formatEntryLabel(e, idx)}
              </option>
            ))}
          </select>
        </div>

        {/* Side-by-side panels with color-coded line diff.
            Walter 2026-05-17: green = only in this side, plain = in
            both. On narrow viewports (mobile) the panes stack
            vertically so each gets the full width — the side-by-side
            squeezed both to unreadable widths. */}
        <div style={{
          display: 'grid',
          gridTemplateColumns: isNarrow ? '1fr' : '1fr 1fr',
          gridAutoRows: isNarrow ? 'minmax(0, 1fr)' : undefined,
          gap: '1px', background: 'var(--border)',
          flex: 1, minHeight: 0, overflow: 'hidden',
        }}>
          <VersionPanel
            label="current (live)"
            title={currentTitle}
            taggedLines={diff?.aTagged}
            sideKind="a"
          />
          <VersionPanel
            label={selectedEntry ? 'selected previous version' : 'previous version'}
            title={selectedEntry?.title}
            taggedLines={diff?.bTagged}
            sideKind="b"
            emptyHint={
              entries === null ? 'loading…'
              : entries && entries.length === 0
                ? 'no previous versions yet — they’ll appear here if another device ever overwrites this list while you’re editing it'
                : null
            }
          />
        </div>

        {/* Diff legend — explains the colour coding. */}
        <div style={{
          padding: '4px 18px',
          background: 'var(--surface2)',
          borderTop: '1px solid var(--border)',
          fontFamily: 'var(--mono)', fontSize: '10px',
          color: 'var(--text-dimmer)',
          display: 'flex', gap: '14px', alignItems: 'center',
          flexShrink: 0,
        }}>
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: '4px' }}>
            <span style={diffSwatchStyle()}>&nbsp;</span> only on this side
          </span>
          <span>plain · in both</span>
        </div>

        {/* Merge diagnostic (Walter 2026-07-26) — READ-ONLY. For a conflict
            entry, surface the hidden 3-way BASE + the live merge verdict so one
            screenshot explains why it wasn't auto-merged (base stale/empty vs.
            genuine overlap). No behaviour change; remove once understood. */}
        {selectedEntry && (selectedEntry.reason === 'conflict' || selectedEntry.baseContent != null) && (() => {
          const base = selectedEntry.baseContent;
          const mine = currentContent ?? '';
          const theirs = selectedEntry.content ?? '';
          const firstDiff = (a, b) => {
            const la = String(a).split('\n'), lb = String(b).split('\n');
            const n = Math.max(la.length, lb.length);
            for (let i = 0; i < n; i++) if (la[i] !== lb[i]) return `line ${i + 1}`;
            return 'identical';
          };
          let verdict = 'no base recorded on this conflict (created before the 3-way base was stored)';
          if (typeof base === 'string') {
            let clean = null, conflicts = null;
            try { const r = mergeThreeWay(base, mine, theirs); clean = r.clean; conflicts = r.conflicts; } catch { /* ignore */ }
            verdict = `auto-merge verdict: ${clean === null ? '?' : clean ? 'CLEAN ✓ (should have merged silently)' : `NOT clean — ${conflicts ?? '?'} clashing region${conflicts === 1 ? '' : 's'}`}`
              + ` · base ${base.length} chars`
              + ` · base↔current first differs at ${firstDiff(base, mine)}`
              + ` · base↔previous first differs at ${firstDiff(base, theirs)}`;
          }
          return (
            <div style={{
              padding: '8px 18px', borderTop: '1px solid var(--border)',
              background: 'var(--surface2)', fontFamily: 'var(--mono)',
              fontSize: '10px', color: 'var(--text-dim)', flexShrink: 0,
            }}>
              <div style={{ marginBottom: 4, color: 'var(--text-dimmer)' }}>⚙ merge diagnostic (screenshot this) — {verdict}</div>
              <div style={{ color: 'var(--text-dimmer)' }}>base the 3-way merge compared against:</div>
              <pre style={{
                margin: '2px 0 0', maxHeight: 90, overflow: 'auto',
                whiteSpace: 'pre-wrap', wordBreak: 'break-word',
                background: 'var(--bg)', border: '1px solid var(--border)',
                borderRadius: 4, padding: '6px 8px',
              }}>{typeof base === 'string' ? (base || '(empty string)') : '(none stored)'}</pre>
            </div>
          );
        })()}

        {/* Error band */}
        {error && (
          <div style={{
            padding: '8px 18px',
            background: 'var(--surface2)',
            borderTop: '1px solid var(--border)',
            fontFamily: 'var(--mono)', fontSize: '11px',
            color: 'var(--accent)',
            flexShrink: 0,
          }}>
            {error}
          </div>
        )}

        {/* Actions */}
        <div style={{
          padding: '12px 18px',
          borderTop: '1px solid var(--border)',
          background: 'var(--surface2)',
          display: 'flex', gap: '8px', justifyContent: 'space-between',
          alignItems: 'center', flexShrink: 0,
        }}>
          <div style={{
            fontFamily: 'var(--mono)', fontSize: '10px',
            color: 'var(--text-dimmer)',
          }}>
            {selectedEntry && formatEntryMeta(selectedEntry)}
          </div>
          <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', justifyContent: 'flex-end' }}>
            <ActionBtn onClick={onClose} disabled={busy}>close</ActionBtn>
            <ActionBtn
              onClick={handleSaveAsNewList}
              disabled={busy || !selectedEntry}
            >
              {busy ? '…' : 'save previous as separate list'}
            </ActionBtn>
            <ActionBtn
              onClick={handleRestore}
              disabled={busy || !selectedEntry}
            >
              {busy ? '…' : 'replace current with previous version'}
            </ActionBtn>
            {canMergeBoth && (
              <ActionBtn
                onClick={handleMergeBoth}
                disabled={busy}
                title="combine both versions automatically — disjoint edits merge, only same-line clashes keep both"
              >
                {busy ? '…' : 'merge both (beta)'}
              </ActionBtn>
            )}
            <ActionBtn
              onClick={handleMarkResolved}
              disabled={busy}
              accent
            >
              {busy ? '…' : 'use current and mark resolved'}
            </ActionBtn>
          </div>
        </div>
      </div>
    </div>
  );
}

function VersionPanel({ label, title, taggedLines, sideKind, emptyHint }) {
  // taggedLines comes from diffLines(...). `sideKind` is 'a' (left/
  // current) or 'b' (right/previous). On the 'a' side, lines tagged
  // 'removed' are unique to the left pane (green). 'added' tags only
  // exist on the 'b' side. The styling is symmetric in feel — both
  // panes highlight what's UNIQUE to them in green and gesture at
  // missing lines from the other side with a faint dotted marker.
  const empty = !taggedLines || taggedLines.length === 0
    || taggedLines.every(l => l.text === '');
  return (
    <div style={{
      background: 'var(--surface)',
      display: 'flex', flexDirection: 'column',
      minHeight: 0, minWidth: 0, overflow: 'hidden',
    }}>
      <div style={{
        padding: '8px 14px',
        fontFamily: 'var(--mono)', fontSize: '10px', fontWeight: 600,
        color: 'var(--text-dim)', letterSpacing: '0.5px',
        borderBottom: '1px solid var(--border)',
        background: 'var(--bg)',
        flexShrink: 0,
      }}>
        {label}
      </div>
      <div style={{
        padding: '8px 14px',
        fontFamily: 'var(--mono)', fontSize: '11px', fontWeight: 600,
        color: title ? 'var(--text)' : 'var(--text-dimmer)',
        fontStyle: title ? 'normal' : 'italic',
        borderBottom: '1px solid var(--border)',
        flexShrink: 0,
      }}>
        {title || '(no title)'}
      </div>
      <div style={{
        // Walter 2026-05-17: removed maxHeight cap so panels can
        // breathe when the modal is stacked vertically on mobile.
        // The parent grid + modal's overall maxHeight bound the
        // overall surface; each pane just fills what's available.
        flex: 1, margin: 0, padding: '12px 14px',
        fontFamily: 'var(--mono)', fontSize: '11px',
        color: 'var(--text)', lineHeight: 1.6,
        overflowY: 'auto', minHeight: '140px',
      }}>
        {empty ? (
          <span style={{ color: 'var(--text-dimmer)', fontStyle: 'italic' }}>
            {emptyHint || '(empty)'}
          </span>
        ) : (
          taggedLines.map((l, idx) => (
            <DiffLine key={idx} line={l} sideKind={sideKind} />
          ))
        )}
      </div>
    </div>
  );
}

// DiffLine — one rendered line with per-tag styling.
// For the 'a' (left/current) pane: lines tagged 'removed' are
// UNIQUE-to-a, rendered with the additive (green) treatment because
// they're what's ONLY on this side.
// For the 'b' (right/previous) pane: lines tagged 'added' are
// UNIQUE-to-b, same additive treatment.
function DiffLine({ line, sideKind }) {
  const uniqueToThisSide =
    (sideKind === 'a' && line.tag === 'removed')
    || (sideKind === 'b' && line.tag === 'added');
  const blank = line.text === '';
  if (uniqueToThisSide) {
    return (
      <div style={{
        background: 'rgba(80, 170, 80, 0.18)',
        borderLeft: '2px solid rgba(80, 170, 80, 0.6)',
        padding: '0 6px',
        margin: '0 -6px',
        whiteSpace: 'pre-wrap',
        wordBreak: 'break-word',
        minHeight: '1.6em',
      }}>
        {blank ? ' ' : line.text}
      </div>
    );
  }
  return (
    <div style={{
      whiteSpace: 'pre-wrap',
      wordBreak: 'break-word',
      minHeight: '1.6em',
    }}>
      {blank ? ' ' : line.text}
    </div>
  );
}

// Diff swatch — small colored square used in the legend below the
// panels.
function diffSwatchStyle() {
  return {
    display: 'inline-block',
    width: '10px', height: '10px',
    background: 'rgba(80, 170, 80, 0.35)',
    border: '1px solid rgba(80, 170, 80, 0.6)',
  };
}

function ActionBtn({ children, onClick, disabled, accent }) {
  return (
    <button
      onClick={onClick}
      disabled={disabled}
      style={{
        background: accent ? 'var(--accent)' : 'none',
        border: `1px solid ${accent ? 'var(--accent)' : 'var(--border-bright)'}`,
        color: accent ? '#000' : 'var(--text)',
        fontFamily: 'var(--mono)', fontSize: '12px', fontWeight: 500,
        padding: '8px 16px', borderRadius: 'var(--radius)',
        cursor: disabled ? 'default' : 'pointer',
        opacity: disabled ? 0.5 : 1,
        transition: 'all 0.15s',
      }}
    >
      {children}
    </button>
  );
}

function formatEntryLabel(entry, idx) {
  const ts = entry.replacedAt?.toDate?.() || entry.replacedAt;
  const dateStr = ts
    ? new Date(ts).toLocaleString(undefined, {
        month: 'short', day: 'numeric',
        hour: 'numeric', minute: '2-digit',
      })
    : `version ${idx + 1}`;
  const reason = {
    restore: ' (pre-restore)',
    resolution: ' (kept on resolve)',
    'shrink-guard': ' (large deletion)',
    conflict: ' (conflict)',
    daily: ' (auto backup)',   // Walter 2026-09-02: every ~10 min of editing now, not daily
    // Walter 2026-09-03: the text as it stood just before a SILENT automatic
    // merge. Those used to leave nothing behind at all — see archivePreMerge.
    'pre-merge': ' (before auto-merge)',
    // Walter 2026-08-07 (merge-on-receive): this device's own buffer, archived
    // at adoption time because it had diverged from the version that arrived
    // and the two clashed on the same line.
    divergence: ' (unsynced edits)',
    // Walter 2026-08-10: conflictResolver.processOfflineQueue writes this when
    // a QUEUED edit replays and clashes. It was the only reason with no label,
    // which is exactly the entry we couldn't identify on his 2026-08-10
    // conflict — the absent suffix was the clue that named the path.
    'offline-merge': ' (queued edit replayed)',
  }[entry.reason] || '';
  return `${dateStr}${reason}`;
}

function formatEntryMeta(entry) {
  const ts = entry.replacedAt?.toDate?.() || entry.replacedAt;
  if (!ts) return '';
  return `replaced ${new Date(ts).toLocaleString()}`;
}
